import React, { useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import { Alert, Form } from 'react-bootstrap'
import { Button } from 'primereact/button'
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { Tag } from 'primereact/tag'
import FlowCards from './FlowCards'
import NewTabLink from './NewTabLink'
import LayoutHelpLink from './LayoutHelpLink'
import { integrationPath } from './implementedRoutes'
import CatalogLoadingOverlay from './CatalogLoadingOverlay'
import AddIntegrationDialog from './AddIntegrationDialog'
import FlowTypeUsage from './FlowTypeUsage'
import OverLimitIndicator from './OverLimitIndicator'
import { REMOVE_FROM_LAYOUT_ACCEPT, VISUAL_BUILDER_KEEP_FLOWS } from './layoutBuilderCopy'
import { BLANK_LAYOUT_NAME_MESSAGE } from './nameRules'
import RemoveFromLayoutMessage from './RemoveFromLayoutMessage'
import GuideNote from './GuideNote'
import { usageForCatalogFlowTypes } from './flowLimits'
import { isVisualBuilderIntegration } from './implementReviewLimits'
import { visualBuilderFlowPrompt } from './visualBuilderPrompt'
import { selectableIntegrations } from './flowTypeCuration'
import { converterApplications } from './visualBuilderComponents'
import { flowImplementationNames, integrationChangeNote } from './implementationChanges'
import { isFlatVisualBuilderSku, LEGACY_VISUAL_BUILDER_SKU } from '../../utils/visualBuilderSku'
import {
  eligibleApplications,
  findFlowType,
  findIntegrationType,
  PROTOCOLS,
  sideIsCompatible,
  UNKNOWN_APPLICATION_ID,
  visualBuilderDestinationAdapters,
  visualBuilderFlowType,
  visualBuilderSourceAdapters
} from './catalog'
import { normalizeIntegrations } from './layoutModel'
import './layoutBuilder.css'

const SAVED_VISIBLE_MS = 2200
const SAVED_FADE_MS = 300

const blankSide = () => ({
  application_id: null,
  application_label: null,
  file_type_id: null,
  file_type_label: null,
  protocol: null,
  unknown: false
})

const blankFlow = () => ({
  // Names may repeat, so every new flow draft receives its own persisted identity.
  flow_uuid: crypto.randomUUID(),
  flow_type_id: null,
  flow_type_label: null,
  name: null,
  source: blankSide(),
  destination: blankSide(),
  notes: null
})

const valueOrNull = value => value || null
const clearCatalogSelection = side => ({ ...blankSide(), protocol: side?.protocol || null })
const fileTypeOptionValue = (applicationId, fileTypeId) => JSON.stringify([applicationId, fileTypeId])

const integrationTypeForFlow = (catalog, integration, flow) => {
  const currentIntegrationType = findIntegrationType(catalog, integration.integration_type_id)
  if (!flow?.flow_type_id || isFlatVisualBuilderSku(currentIntegrationType?.id) || findFlowType(currentIntegrationType, flow.flow_type_id)) return currentIntegrationType

  const retainedFlowType = catalog.integrations
    .map(integrationType => findFlowType(integrationType, flow.flow_type_id))
    .find(Boolean)
  if (!retainedFlowType) return currentIntegrationType

  return {
    ...(currentIntegrationType || {
      id: integration.integration_type_id,
      name: integration.integration_type_label
    }),
    flows: [...(currentIntegrationType?.flows || []), retainedFlowType]
  }
}

const overLimitTypes = (catalog, integration) =>
  usageForCatalogFlowTypes(integration.flows, findIntegrationType(catalog, integration.integration_type_id)).filter(item => item.over)

function SideEditor ({ label, direction, side, flowType, allowedAdapterIds, applications, onChange, readOnly }) {
  const options = eligibleApplications(
    applications,
    allowedAdapterIds || (direction === 'source' ? flowType?.sourceAdapters : flowType?.destinationAdapters),
    side
  )
  const selectedId = side.unknown ? UNKNOWN_APPLICATION_ID : side.application_id
  const selectedApplication = options.find(option => option.id === selectedId)
  const fileTypeApplications = selectedApplication && !side.unknown
    ? [selectedApplication]
    : options.filter(option => option.id !== UNKNOWN_APPLICATION_ID)
  const fileTypeOptions = fileTypeApplications.flatMap(application =>
    (application.fileTypes || []).map(fileType => ({
      ...fileType,
      application_id: application.id,
      application_label: application.name,
      label: selectedApplication ? fileType.name : `${fileType.name} — ${application.name}`,
      value: fileTypeOptionValue(application.id, fileType.id)
    }))
  )
  const selectedFileTypeValue = side.application_id && side.file_type_id
    ? fileTypeOptionValue(side.application_id, side.file_type_id)
    : null

  const selectApplication = applicationId => {
    if (applicationId === UNKNOWN_APPLICATION_ID) {
      onChange({ ...clearCatalogSelection(side), unknown: true })
      return
    }
    const application = options.find(option => option.id === applicationId)
    onChange({
      ...side,
      application_id: application?.id || null,
      application_label: application?.name?.replace(' (unavailable)', '') || null,
      file_type_id: null,
      file_type_label: null,
      unknown: false
    })
  }

  const selectFileType = optionValue => {
    const fileType = fileTypeOptions.find(option => option.value === optionValue)
    if (!fileType) {
      onChange({ ...side, file_type_id: null, file_type_label: null })
      return
    }
    onChange({
      ...side,
      application_id: fileType.application_id,
      application_label: fileType.application_label?.replace(' (unavailable)', '') || null,
      file_type_id: fileType.id,
      file_type_label: fileType.name?.replace(' (unavailable)', '') || null,
      unknown: false
    })
  }

  return (
    <fieldset className='layoutBuilder-side'>
      <legend>{label}</legend>
      <label htmlFor={`${direction}-application`}>Application</label>
      <Dropdown
        inputId={`${direction}-application`}
        panelClassName='layoutBuilder-catalog-panel'
        value={selectedId}
        options={options}
        optionLabel='name'
        optionValue='id'
        placeholder='Not specified'
        showClear
        filter
        disabled={readOnly || !flowType}
        onChange={event => selectApplication(event.value)}
      />
      <label htmlFor={`${direction}-file-type`}>File type</label>
      <Dropdown
        inputId={`${direction}-file-type`}
        panelClassName='layoutBuilder-catalog-panel'
        value={selectedFileTypeValue}
        options={fileTypeOptions}
        optionLabel='label'
        optionValue='value'
        placeholder='Not specified'
        showClear
        filter
        disabled={readOnly || !flowType || side.unknown || fileTypeOptions.length === 0}
        onChange={event => selectFileType(event.value)}
      />
      <label htmlFor={`${direction}-protocol`}>Protocol</label>
      <Dropdown
        inputId={`${direction}-protocol`}
        value={side.protocol}
        options={PROTOCOLS}
        placeholder='Not specified'
        showClear
        disabled={readOnly}
        onChange={event => onChange({ ...side, protocol: event.value || null })}
      />
    </fieldset>
  )
}

function FlowModal ({ integrationType, catalog, applications, flow, onClose, onSave, onRemove, readOnly }) {
  const isVisualBuilder = isFlatVisualBuilderSku(integrationType?.id)
  const [draft, setDraft] = useState(() => {
    const initial = _.cloneDeep(flow || blankFlow())
    // Visual Builder has no flow type choice: every flow is the catalog's single vb flow type.
    const vbFlowType = visualBuilderFlowType(integrationType)
    if (vbFlowType && !initial.flow_type_id) {
      initial.flow_type_id = vbFlowType.id
      initial.flow_type_label = vbFlowType.name
    }
    if (flow && !initial.name?.trim()) initial.name = initial.flow_type_label
    return initial
  })
  // Transient UI state only, for LB-20's inline validation: whether the name field has
  // been touched, so the error doesn't show before the user has had a chance to type.
  // This is not naming intent and is never persisted (LB-21 drops the old manual-name flag).
  const [nameTouched, setNameTouched] = useState(false)
  const [pendingFlow, setPendingFlow] = useState(null)

  const nameError = nameTouched && !draft.name?.trim() ? 'Flow name is required' : null

  const flowTypes = integrationType?.flows || []
  const savedFlowTypeMissing = draft.flow_type_id && !findFlowType(integrationType, draft.flow_type_id)
  const flowTypeOptions = savedFlowTypeMissing
    ? [...flowTypes, {
        id: draft.flow_type_id,
        name: `${draft.flow_type_label || 'Saved flow type'} (unavailable)`,
        sourceAdapters: [],
        destinationAdapters: [],
        historical: true
      }]
    : flowTypes
  // Visual Builder flows are always the catalog's vb flow type, whatever was saved.
  const flowType = isVisualBuilder ? visualBuilderFlowType(integrationType) : flowTypeOptions.find(item => item.id === draft.flow_type_id)

  // Visual Builder flows are not tied to one catalog flow type. Their source options are the vb
  // input converters and their destination options the output converters, each side independent
  // (no cross-side narrowing). Without converter data (older/partial catalog), both sides fall back
  // to the catalog-wide adapter union, with the destination narrowed by the chosen source.
  const inputApplications = isVisualBuilder ? converterApplications(integrationType.components, applications, 'inputConverter') : []
  const outputApplications = isVisualBuilder ? converterApplications(integrationType.components, applications, 'outputConverter') : []
  const usesConverters = inputApplications.length > 0 && outputApplications.length > 0
  const fileTypeIds = apps => apps.flatMap(application => application.fileTypes.map(fileType => fileType.id))
  const sourceApplications = usesConverters ? inputApplications : applications
  const destinationApplications = usesConverters ? outputApplications : applications
  const sourceAdapterIds = usesConverters ? fileTypeIds(inputApplications) : isVisualBuilder ? visualBuilderSourceAdapters(catalog) : undefined
  const destinationAdaptersFor = source => usesConverters
    ? fileTypeIds(outputApplications)
    : isVisualBuilder ? visualBuilderDestinationAdapters(catalog, source?.file_type_id) : undefined

  const changeSource = source => {
    const next = { ...draft, source }
    if (isVisualBuilder && !usesConverters) {
      const destination = draft.destination || blankSide()
      const destinationAllowed = { destinationAdapters: destinationAdaptersFor(source) }
      if (!sideIsCompatible(destination, destinationAllowed, 'destination', applications)) next.destination = clearCatalogSelection(destination)
    }
    setDraft(next)
  }

  const applyFlowType = nextFlowType => {
    // Equality with the saved type label is the complete automatic-name contract (LB-21):
    // no manual-intent flag is tracked, even when the matching text was typed by hand.
    const nameFollowsType = draft.flow_type_id
      ? draft.name === draft.flow_type_label
      : !draft.name
    const next = {
      ...draft,
      flow_type_id: nextFlowType.id,
      flow_type_label: nextFlowType.name,
      name: nameFollowsType ? nextFlowType.name : draft.name
    }
    if (!sideIsCompatible(next.source || blankSide(), nextFlowType, 'source', applications)) next.source = clearCatalogSelection(next.source)
    if (!sideIsCompatible(next.destination || blankSide(), nextFlowType, 'destination', applications)) next.destination = clearCatalogSelection(next.destination)
    setDraft(next)
  }

  const selectFlowType = flowTypeId => {
    const nextFlowType = findFlowType(integrationType, flowTypeId)
    if (!nextFlowType) return
    const cleared = []
    if (!sideIsCompatible(draft.source || blankSide(), nextFlowType, 'source', applications)) cleared.push('source')
    if (!sideIsCompatible(draft.destination || blankSide(), nextFlowType, 'destination', applications)) cleared.push('destination')
    if (cleared.length > 0) {
      setPendingFlow({ flowType: nextFlowType, cleared })
    } else {
      applyFlowType(nextFlowType)
    }
  }

  return (
    <>
      <Dialog
        visible
        modal
        blockScroll
        draggable={false}
        resizable={false}
        className='layoutBuilder-flow-dialog'
        onHide={onClose}
        header={flow ? 'Edit flow' : 'Add flow'}
        footer={(
          <div className='layoutBuilder-form-actions'>
            {flow && !readOnly && <Button label='Remove flow' severity='danger' text onClick={onRemove} />}
            <span className='layoutBuilder-form-spacer' />
            <Button label={readOnly ? 'Close' : 'Cancel'} severity='secondary' outlined onClick={onClose} />
            {!readOnly && (
              <Button
                label={flow ? 'Save changes' : 'Add flow'}
                disabled={(!draft.flow_type_id && !isVisualBuilder) || !draft.name?.trim()}
                onClick={() => onSave(draft)}
              />
            )}
          </div>
        )}
      >
        <div className='layoutBuilder-form'>
          {isVisualBuilder && <GuideNote />}
          {!isVisualBuilder && (
            <>
              <label htmlFor='layout-flow-type'>Flow type <span aria-hidden='true'>*</span></label>
              <Dropdown
                inputId='layout-flow-type'
                value={draft.flow_type_id}
                options={flowTypeOptions}
                optionLabel='name'
                optionValue='id'
                placeholder='Choose a flow type'
                filter
                disabled={readOnly}
                onChange={event => selectFlowType(event.value)}
              />
            </>
          )}
          <label htmlFor='layout-flow-name'>Flow Name <span aria-hidden='true'>*</span></label>
          <InputText
            id='layout-flow-name'
            value={draft.name || ''}
            disabled={readOnly}
            className={nameError ? 'p-invalid' : ''}
            aria-describedby={nameError ? 'layout-flow-name-error' : undefined}
            onChange={event => {
              setDraft({ ...draft, name: event.target.value })
              setNameTouched(true)
            }}
          />
          {nameError && <small id='layout-flow-name-error' className='p-error'>{nameError}</small>}
          <div className='layoutBuilder-side-grid'>
            <SideEditor
              label='Source'
              direction='source'
              side={draft.source || blankSide()}
              flowType={flowType}
              allowedAdapterIds={sourceAdapterIds}
              applications={sourceApplications}
              readOnly={readOnly}
              onChange={changeSource}
            />
            <SideEditor
              label='Destination'
              direction='destination'
              side={draft.destination || blankSide()}
              flowType={flowType}
              allowedAdapterIds={destinationAdaptersFor(draft.source)}
              applications={destinationApplications}
              readOnly={readOnly}
              onChange={destination => setDraft({ ...draft, destination })}
            />
          </div>
          <label htmlFor='layout-flow-notes'>Notes <span className='text-muted'>(optional)</span></label>
          <InputTextarea
            id='layout-flow-notes'
            value={draft.notes || ''}
            rows={5}
            autoResize
            disabled={readOnly}
            onChange={event => setDraft({ ...draft, notes: valueOrNull(event.target.value) })}
          />
        </div>
      </Dialog>
      <Dialog
        visible={Boolean(pendingFlow)}
        header='Change flow type?'
        modal
        onHide={() => setPendingFlow(null)}
        footer={(
          <div className='layoutBuilder-dialog-actions'>
            <Button label='Cancel' severity='secondary' outlined onClick={() => setPendingFlow(null)} />
            <Button
              label='Change and clear values'
              onClick={() => {
                applyFlowType(pendingFlow.flowType)
                setPendingFlow(null)
              }}
            />
          </div>
        )}
      >
        Changing the flow type will clear the incompatible {pendingFlow?.cleared.join(' and ')} selection. The flow name and notes will be kept.
      </Dialog>
    </>
  )
}

// The editor's own ConfirmDialog group: the portal's shared <ConfirmDialog /> (MessageCenter) only answers
// ungrouped calls, and the public planner has none.
const CONFIRM_GROUP = 'layoutBuilder'
const CONFIRM_DIALOG_STYLE = { width: 'min(32rem, calc(100vw - 2rem))' }

const confirmRemoveFromLayout = ({ header, name, implementedNames, accept }) => confirmDialog({
  group: CONFIRM_GROUP,
  header,
  message: <RemoveFromLayoutMessage name={name} implementedNames={implementedNames} />,
  icon: 'pi pi-exclamation-triangle',
  acceptLabel: REMOVE_FROM_LAYOUT_ACCEPT,
  rejectLabel: 'Cancel',
  acceptClassName: 'p-button-danger',
  accept
})

// The integration's status tag, shown beside its type at the top of the panel, with a muted note when
// the planned integration changed after one of its implementations.
function ImplementedStatus ({ implementations }) {
  if (implementations.length === 0) return null
  const changeNote = integrationChangeNote(implementations)
  return (
    <>
      <Tag severity='success' value={implementations.length === 1 ? 'Implemented' : `Implemented ×${implementations.length}`} />
      {changeNote && <span className='layoutBuilder-changed-since-implemented'>{changeNote}</span>}
    </>
  )
}

// Implement integration first, so it never moves as implementations are added, then an Open link per
// implementation (by name). It is always offered: implementing again creates a second, separate integration.
function ImplementedActions ({ companyUUID, implementations, loading, disabled, onImplement }) {
  return (
    <>
      <Button label='Implement integration' icon='pi pi-play' loading={loading} disabled={disabled} onClick={onImplement} />
      {implementations.map(implementation => (
        <NewTabLink
          key={implementation.partner_uuid}
          label={`Open ${implementation.partner_name}`}
          href={integrationPath(companyUUID, implementation.partner_uuid)}
        />
      ))}
    </>
  )
}

export default function LayoutBuilderEditor ({ layout, catalog, readOnly, saveState, onChange, onDelete, isNew = false, titleActions = null, catalogLoading = false, catalogError = null, catalogRefetch = null, onImplement = null, companyUUID = null, onAddFlow = null }) {
  const integrations = useMemo(() => normalizeIntegrations(layout.integrations), [layout.integrations])
  const [activeIntegrationId, setActiveIntegrationId] = useState(() => normalizeIntegrations(layout.integrations)[0]?.integration_uuid ?? null)
  const [addIntegrationOpen, setAddIntegrationOpen] = useState(false)
  const [flowEditor, setFlowEditor] = useState(null)
  const [confirmation, setConfirmation] = useState(null)
  const [pendingIntegrationType, setPendingIntegrationType] = useState(null)
  const [integrationRename, setIntegrationRename] = useState(null)
  // Names the implement control whose review is being prepared ('layout', an integration or a flow card),
  // so that control shows its loading state while every implement action is disabled.
  const [preparingReview, setPreparingReview] = useState(null)
  const [nameEditing, setNameEditing] = useState(false)
  const [nameDraft, setNameDraft] = useState(layout.name)
  const [savedPhase, setSavedPhase] = useState(saveState === 'saved' ? 'visible' : 'hidden')
  const flowEditorReturnFocus = useRef(null)

  const activeIndex = Math.max(0, integrations.findIndex(integration => integration.integration_uuid === activeIntegrationId))

  useEffect(() => {
    if (!nameEditing || readOnly) setNameDraft(layout.name)
    if (readOnly) setNameEditing(false)
  }, [layout.name, nameEditing, readOnly])

  useEffect(() => {
    if (readOnly) setConfirmation(null)
  }, [readOnly])

  useEffect(() => {
    if (saveState !== 'saved') {
      setSavedPhase('hidden')
      return
    }

    setSavedPhase('visible')
    const fadeTimer = window.setTimeout(() => setSavedPhase('fading'), SAVED_VISIBLE_MS)
    const hideTimer = window.setTimeout(() => setSavedPhase('hidden'), SAVED_VISIBLE_MS + SAVED_FADE_MS)
    return () => {
      window.clearTimeout(fadeTimer)
      window.clearTimeout(hideTimer)
    }
  }, [saveState])

  const openFlowEditor = (editor, trigger) => {
    flowEditorReturnFocus.current = trigger || document.activeElement
    setFlowEditor(editor)
  }

  const closeFlowEditor = () => {
    const returnFocus = flowEditorReturnFocus.current
    setFlowEditor(null)
    returnFocus?.focus()
  }

  const updateIntegration = (integrationId, nextIntegration) => {
    onChange({ ...layout, integrations: integrations.map(item => item.integration_uuid === integrationId ? nextIntegration : item) })
  }

  const applyIntegrationType = (integrationId, nextIntegrationType) => {
    const integration = integrations.find(item => item.integration_uuid === integrationId)
    // Equality with the saved type label is the complete automatic-name contract (LB-21):
    // no persisted manual-intent flag is tracked, even when the matching text was typed by hand.
    const nameFollowsType = integration.name === integration.integration_type_label
    updateIntegration(integrationId, {
      ...integration,
      integration_type_id: nextIntegrationType.id,
      integration_type_label: nextIntegrationType.name,
      name: nameFollowsType ? nextIntegrationType.name : integration.name
    })
  }

  const selectIntegrationType = (integrationId, integrationTypeId) => {
    const integration = integrations.find(item => item.integration_uuid === integrationId)
    const nextIntegrationType = findIntegrationType(catalog, integrationTypeId)
    // v3 is never selectable (picking its own saved, unavailable option again is a no-op).
    if (!nextIntegrationType || nextIntegrationType.id === LEGACY_VISUAL_BUILDER_SKU) return
    const incompatibleFlows = integration.flows.filter(flow => !findFlowType(nextIntegrationType, flow.flow_type_id))
    if (incompatibleFlows.length > 0) {
      setPendingIntegrationType({ integrationId, nextIntegrationType, incompatibleFlows })
      return
    }
    applyIntegrationType(integrationId, nextIntegrationType)
  }

  const cancelNameEdit = () => {
    setNameDraft(layout.name)
    setNameEditing(false)
  }

  const commitNameEdit = event => {
    event.preventDefault()
    if (readOnly) {
      cancelNameEdit()
      return
    }
    if (!nameDraft.trim()) return
    if (nameDraft !== layout.name) onChange({ ...layout, name: nameDraft })
    setNameEditing(false)
  }

  // Shared by the flow cards and the flow dialog's Remove flow.
  const removeFlow = (integrationId, flow) => {
    setFlowEditor(null)
    const integration = integrations.find(item => item.integration_uuid === integrationId)
    confirmRemoveFromLayout({
      header: 'Remove flow?',
      name: flow.name || flow.flow_type_label || 'this flow',
      implementedNames: flowImplementationNames(integration.implementations || [], flow.flow_uuid),
      accept: () => {
        const current = integrations.find(item => item.integration_uuid === integrationId)
        updateIntegration(integrationId, {
          ...current,
          flows: current.flows.filter(item => item.flow_uuid !== flow.flow_uuid)
        })
      }
    })
  }

  // Implement actions belong to the writable workspace editor only (never viewers or the public planner).
  const implementActions = Boolean(!readOnly && onImplement && companyUUID)

  // The workspace saves and refetches the layout before its review dialog can open, which can take a
  // moment: `control` keeps the clicked button loading until then.
  const prepareReview = async (control, openReview) => {
    setPreparingReview(control)
    try {
      await openReview()
    } finally {
      setPreparingReview(null)
    }
  }

  // integrationId null reviews every integration of the layout that is not implemented yet.
  const prepareImplementReview = integrationId =>
    prepareReview(integrationId ? `integration:${integrationId}` : 'layout', () => onImplement(integrationId))

  const removeIntegration = integrationId => {
    const integration = integrations.find(item => item.integration_uuid === integrationId)
    confirmRemoveFromLayout({
      header: 'Remove integration?',
      name: integration.name,
      implementedNames: (integration.implementations || []).map(item => item.partner_name),
      accept: () => {
        const removedIndex = integrations.findIndex(item => item.integration_uuid === integrationId)
        const remaining = integrations.filter(item => item.integration_uuid !== integrationId)
        onChange({ ...layout, integrations: remaining })
        setActiveIntegrationId(remaining[Math.max(0, removedIndex - 1)]?.integration_uuid ?? null)
      }
    })
  }

  return (
    <CatalogLoadingOverlay loading={catalogLoading}>
      <div className='layoutBuilder-editor'>
        {readOnly && <Alert variant='secondary'>You can view this layout. An Owner or Editor can make changes.</Alert>}
        <div className='layoutBuilder-title-row'>
          {isNew
            ? (
              <Form.Group className='layoutBuilder-name'>
                <Form.Label htmlFor='layout-builder-name'>Layout name</Form.Label>
                <Form.Control
                  id='layout-builder-name'
                  value={nameDraft}
                  readOnly={readOnly}
                  isInvalid={!nameDraft.trim()}
                  aria-describedby={nameDraft.trim() ? undefined : 'layout-builder-name-error'}
                  onChange={event => {
                    // The field may sit blank while typing, but a blank name is never saved: the last name stays.
                    setNameDraft(event.target.value)
                    if (event.target.value.trim()) onChange({ ...layout, name: event.target.value })
                  }}
                />
                {!nameDraft.trim() && <small id='layout-builder-name-error' className='text-danger'>{BLANK_LAYOUT_NAME_MESSAGE}</small>}
              </Form.Group>
              )
            : nameEditing && !readOnly
              ? (
                <Form className='layoutBuilder-name layoutBuilder-name-edit' onSubmit={commitNameEdit}>
                  <Form.Group>
                    <Form.Label className='layoutBuilder-visually-hidden' htmlFor='layout-builder-name'>Layout name</Form.Label>
                    <Form.Control
                      id='layout-builder-name'
                      value={nameDraft}
                      autoFocus
                      onChange={event => setNameDraft(event.target.value)}
                      onKeyDown={event => {
                        if (event.key === 'Escape') cancelNameEdit()
                      }}
                    />
                  </Form.Group>
                  <div className='layoutBuilder-name-actions'>
                    <Button type='button' icon='pi pi-times' aria-label='Cancel layout name edit' severity='secondary' text rounded onClick={cancelNameEdit} />
                    <Button type='submit' icon='pi pi-check' aria-label='Save layout name' text rounded disabled={!nameDraft.trim()} />
                  </div>
                </Form>
                )
              : (
                <div className='layoutBuilder-name layoutBuilder-name-display'>
                  <h1>{layout.name}</h1>
                  {!readOnly && (
                    <Button
                      icon='pi pi-pencil'
                      aria-label='Edit layout name'
                      text
                      rounded
                      onClick={() => {
                        setNameDraft(layout.name)
                        setNameEditing(true)
                      }}
                    />
                  )}
                </div>
                )}
          <span className={`layoutBuilder-save-state ${!saveState || (saveState === 'saved' && savedPhase === 'hidden') ? 'hidden' : ''} ${saveState === 'error' ? 'error' : ''} ${savedPhase === 'fading' ? 'fading' : ''}`} role='status'>
            {saveState === 'saving' && 'Saving…'}
            {saveState === 'saved' && savedPhase !== 'hidden' && 'Saved'}
            {saveState === 'error' && 'Changes not saved'}
          </span>
          {!isNew && titleActions && <div className='layoutBuilder-title-actions'>{titleActions}</div>}
        </div>
        <p className='text-muted'>Plan integrations one tab at a time. Choose a flow type and name; add other details when you know them. <LayoutHelpLink /></p>

        {integrations.length === 0 && (
          <div className='layoutBuilder-empty'>
            <h2>Start with an integration type</h2>
            <p>Add information as the plan takes shape. Missing optional details are fine.</p>
            {!readOnly && <Button label='Add integration' icon='pi pi-plus' onClick={() => setAddIntegrationOpen(true)} />}
          </div>
        )}

        {integrations.length > 0 && (
          <>
            <div className='layoutBuilder-integrations-heading'>
              <h2 className='layoutBuilder-integrations-title'>Integrations</h2>
              {implementActions && (
                <Button label='Implement layout' icon='pi pi-play' loading={preparingReview === 'layout'} disabled={Boolean(preparingReview)} onClick={() => prepareImplementReview(null)} />
              )}
            </div>
            <div className='layoutBuilder-integration-tabs' role='tablist' aria-label='Integrations'>
              {integrations.map((integration, index) => (
                <div className={`layoutBuilder-integration-tab ${activeIndex === index ? 'active' : ''}`} key={integration.integration_uuid} role='presentation'>
                  <button
                    type='button'
                    role='tab'
                    id={`integration-tab-${integration.integration_uuid}`}
                    aria-controls={`integration-panel-${integration.integration_uuid}`}
                    aria-selected={activeIndex === index}
                    tabIndex={activeIndex === index ? 0 : -1}
                    onClick={() => setActiveIntegrationId(integration.integration_uuid)}
                    onKeyDown={event => {
                      const keys = {
                        ArrowRight: integrations[(index + 1) % integrations.length],
                        ArrowLeft: integrations[(index + integrations.length - 1) % integrations.length],
                        Home: integrations[0],
                        End: integrations[integrations.length - 1]
                      }
                      if (!(event.key in keys)) return
                      event.preventDefault()
                      const nextIntegration = keys[event.key]
                      setActiveIntegrationId(nextIntegration.integration_uuid)
                      document.getElementById(`integration-tab-${nextIntegration.integration_uuid}`)?.focus()
                    }}
                  >{integration.name}
                  </button>
                  <OverLimitIndicator overLimit={overLimitTypes(catalog, integration)} />
                  {!readOnly && <Button icon='pi pi-pencil' aria-label={`Rename ${integration.name}`} text onClick={() => setIntegrationRename({ integrationUuid: integration.integration_uuid, name: integration.name })} />}
                </div>
              ))}
              {!readOnly && <Button label='Add integration' icon='pi pi-plus' text onClick={() => setAddIntegrationOpen(true)} />}
            </div>
            {integrations.map((integration, integrationIndex) => {
              const integrationType = findIntegrationType(catalog, integration.integration_type_id)
              // A Visual Builder integration's flows are built in Visual Builder (never added to an
              // implementation from here), starting from an AI prompt for each planned flow.
              const visualBuilderActions = implementActions && isVisualBuilderIntegration(integration)
              // The legacy Visual Builder SKU (v3) is never offered, as in the picker. An integration
              // already saved with a type that is not offered (v3, or one gone from the catalog) keeps
              // that value visible as an unavailable option so it still renders.
              const offeredTypes = selectableIntegrations(catalog)
              const savedTypeIsOffered = offeredTypes.some(type => type.id === integration.integration_type_id)
              const integrationTypeOptions = integration.integration_type_id && !savedTypeIsOffered
                ? [...offeredTypes, {
                    id: integration.integration_type_id,
                    name: `${integrationType?.name || integration.integration_type_label || 'Saved integration type'} (unavailable)`,
                    flows: [],
                    historical: true
                  }]
                : offeredTypes
              return (
                <div className='layoutBuilder-integration-panel' role='tabpanel' id={`integration-panel-${integration.integration_uuid}`} aria-labelledby={`integration-tab-${integration.integration_uuid}`} hidden={activeIndex !== integrationIndex} key={integration.integration_uuid}>
                  <div className='layoutBuilder-integration-heading'>
                    <div className='layoutBuilder-integration-fields'>
                      <div className='layoutBuilder-integration-type'>
                        <div className='layoutBuilder-integration-type-heading'>
                          <label htmlFor={`layout-integration-type-${integration.integration_uuid}`}>Integration type</label>
                          {implementActions && <ImplementedStatus implementations={integration.implementations || []} />}
                        </div>
                        <Dropdown
                          inputId={`layout-integration-type-${integration.integration_uuid}`}
                          value={integration.integration_type_id}
                          options={integrationTypeOptions}
                          optionLabel='name'
                          optionValue='id'
                          disabled={readOnly}
                          onChange={event => selectIntegrationType(integration.integration_uuid, event.value)}
                        />
                      </div>
                      <p>{integrationType?.description || 'This saved integration type is unavailable in the current catalog.'}</p>
                      {isFlatVisualBuilderSku(integration.integration_type_id) && <GuideNote />}
                    </div>
                    {!readOnly && implementActions && (
                      <div className='layoutBuilder-integration-implement-actions'>
                        <ImplementedActions
                          implementations={integration.implementations || []}
                          loading={preparingReview === `integration:${integration.integration_uuid}`}
                          disabled={Boolean(preparingReview)}
                          onImplement={() => prepareImplementReview(integration.integration_uuid)}
                          companyUUID={companyUUID}
                        />
                      </div>
                    )}
                  </div>
                  <FlowTypeUsage flows={integration.flows} integrationType={integrationType} />
                  {integration.flows.length === 0 && (
                    <div className='layoutBuilder-empty compact'>
                      <h3>No flows yet</h3>
                      <p>Choose a flow type and name now. Connection details can stay blank.</p>
                      {!readOnly && <Button label='Add flow' icon='pi pi-plus' onClick={event => openFlowEditor({ integrationId: integration.integration_uuid, flow: null }, event.currentTarget)} />}
                    </div>
                  )}
                  {integration.flows.length > 0 && (
                    <>
                      {!readOnly && <Button className='layoutBuilder-add-flow' label='Add flow' icon='pi pi-plus' outlined onClick={event => openFlowEditor({ integrationId: integration.integration_uuid, flow: null }, event.currentTarget)} />}
                      <FlowCards
                        flows={integration.flows}
                        integrationType={integrationType}
                        readOnly={readOnly}
                        onOpen={(flow, trigger) => openFlowEditor({ integrationId: integration.integration_uuid, flow }, trigger)}
                        implementations={integration.implementations || []}
                        companyUUID={implementActions ? companyUUID : null}
                        onAddFlow={implementActions && onAddFlow && !visualBuilderActions
                          ? (implementation, flow) => prepareReview(`flow:${flow.flow_uuid}`, () => onAddFlow(integration.integration_uuid, {
                              partnerUuid: implementation.partner_uuid,
                              partnerName: implementation.partner_name,
                              plannedFlowUuid: flow.flow_uuid
                            }))
                          : null}
                        preparingFlowUuid={preparingReview?.startsWith('flow:') ? preparingReview.slice('flow:'.length) : null}
                        addFlowDisabled={Boolean(preparingReview)}
                        aiPrompt={visualBuilderActions
                          ? flow => visualBuilderFlowPrompt({ integrationName: integration.name, flow, integrationType, applications: catalog?.applications })
                          : null}
                        onRemove={readOnly ? null : flow => removeFlow(integration.integration_uuid, flow)}
                      />
                    </>
                  )}
                  {!readOnly && (
                    <div className='layoutBuilder-integration-actions'>
                      <Button
                        label='Remove integration from layout'
                        severity='danger'
                        outlined
                        onClick={() => removeIntegration(integration.integration_uuid)}
                      />
                    </div>
                  )}
                </div>
              )
            })}

          </>
        )}

        <Dialog
          visible={Boolean(integrationRename) && !readOnly}
          header='Rename integration'
          modal
          onHide={() => setIntegrationRename(null)}
          footer={(
            <div className='layoutBuilder-dialog-actions'>
              <Button label='Cancel' severity='secondary' outlined onClick={() => setIntegrationRename(null)} />
              <Button
                label='Apply name'
                disabled={!integrationRename?.name?.trim()}
                onClick={() => {
                  const integration = integrations.find(item => item.integration_uuid === integrationRename.integrationUuid)
                  updateIntegration(integrationRename.integrationUuid, { ...integration, name: integrationRename.name })
                  setIntegrationRename(null)
                }}
              />
            </div>
        )}
        >
          <div className='layoutBuilder-form'>
            <label htmlFor='layout-integration-rename'>Integration name</label>
            <InputText id='layout-integration-rename' autoFocus value={integrationRename?.name || ''} onChange={event => setIntegrationRename({ ...integrationRename, name: event.target.value })} />
          </div>
        </Dialog>

        <AddIntegrationDialog
          visible={addIntegrationOpen}
          catalog={catalog}
          catalogLoading={catalogLoading}
          catalogError={catalogError}
          catalogRefetch={catalogRefetch}
          onHide={() => setAddIntegrationOpen(false)}
          onAdd={integration => {
            onChange({ ...layout, integrations: [...integrations, integration] })
            setActiveIntegrationId(integration.integration_uuid)
          }}
        />

        {flowEditor && (() => {
          const integration = integrations.find(item => item.integration_uuid === flowEditor.integrationId)
          return (
            <FlowModal
              integrationType={integrationTypeForFlow(catalog, integration, flowEditor.flow)}
              catalog={catalog}
              applications={catalog.applications}
              flow={flowEditor.flow}
              readOnly={readOnly}
              onClose={closeFlowEditor}
              onRemove={() => removeFlow(flowEditor.integrationId, flowEditor.flow)}
              onSave={flow => {
                const flows = flowEditor.flow
                  ? integration.flows.map(item => item.flow_uuid === flow.flow_uuid ? flow : item)
                  : [...integration.flows, flow]
                updateIntegration(flowEditor.integrationId, { ...integration, flows })
                closeFlowEditor()
              }}
            />
          )
        })()}

        <Dialog
          visible={Boolean(pendingIntegrationType)}
          header='Change integration type?'
          modal
          onHide={() => setPendingIntegrationType(null)}
          footer={(
            <div className='layoutBuilder-dialog-actions'>
              <Button label='Cancel' severity='secondary' outlined onClick={() => setPendingIntegrationType(null)} />
              <Button
                label='Change type and keep flows'
                onClick={() => {
                  applyIntegrationType(pendingIntegrationType.integrationId, pendingIntegrationType.nextIntegrationType)
                  setPendingIntegrationType(null)
                }}
              />
            </div>
        )}
        >
          {pendingIntegrationType && (isFlatVisualBuilderSku(pendingIntegrationType.nextIntegrationType.id) ? VISUAL_BUILDER_KEEP_FLOWS : `${pendingIntegrationType.incompatibleFlows.length} saved flow${pendingIntegrationType.incompatibleFlows.length === 1 ? ' is' : 's are'} not allowed for ${pendingIntegrationType.nextIntegrationType.name}. ${pendingIntegrationType.incompatibleFlows.length === 1 ? 'It' : 'They'} will be kept with all saved details and marked for review.`)}
        </Dialog>

        <Dialog
          visible={Boolean(confirmation) && !readOnly}
          header={confirmation?.header}
          modal
          onHide={() => setConfirmation(null)}
          footer={(
            <div className='layoutBuilder-dialog-actions'>
              <Button label='Cancel' severity='secondary' outlined onClick={() => setConfirmation(null)} />
              <Button
                label='Remove'
                severity='danger'
                onClick={() => {
                  if (readOnly) return
                  confirmation.accept()
                  setConfirmation(null)
                }}
              />
            </div>
        )}
        >
          {confirmation?.body}
        </Dialog>

        <ConfirmDialog group={CONFIRM_GROUP} style={CONFIRM_DIALOG_STYLE} />

        {!readOnly && onDelete && (
          <div className='layoutBuilder-delete-layout'>
            <Button
              label='Delete layout'
              severity='danger'
              outlined
              onClick={() => setConfirmation({
                header: 'Delete layout?',
                body: 'This layout will become unavailable immediately.',
                accept: onDelete
              })}
            />
          </div>
        )}
      </div>
    </CatalogLoadingOverlay>
  )
}
